// src/lib/previsaoFaturaEquatorial.ts
//
// Quando a fatura da Equatorial sai, contado em DIAS ÚTEIS a partir da leitura.
//
// Em dias corridos a espera varia muito (fim de semana e feriado no meio); em dias úteis ela
// se estabiliza. Medido em 12/10/2026, conferindo a análise que o Vinicius fez sobre o export
// de Datas das faturas:
//   - a APRESENTAÇÃO impressa fica, na mediana, 7 dias úteis depois da leitura. "Próxima
//     leitura + 7 du" acerta com erro de até 1 du em 83% das faturas (mar–set/2026, sem junho).
//     A previsão antiga da Janela — apresentação do mês anterior + 1 mês — acertava 51%;
//   - o PDF chega ANTES da apresentação. Pelo robô de e-mail (2.027 faturas, jul–out/2026):
//     21% até o 3º dia útil, 60% até o 4º, 91% até o 5º e 99% até o 9º;
//   - em mês normal só 1% a 7% das faturas saem depois do 9º dia útil. Nos meses de atraso
//     geral da Equatorial (ago/2025, set/2025, dez/2025, jun/2026) foram 58% a 83%.
//
// O vencimento NÃO sai daqui: "vencimento do mês anterior + 1 mês" (o que a Janela já usa)
// acerta tanto quanto qualquer conta a partir da apresentação prevista (71% × 71%).
//
// Feriados: o calendário de src/lib/reguaPrazosAgrupamento.ts (nacionais, Carnaval,
// Sexta-feira Santa, Corpus Christi e 24/10) mais a tabela `feriados`. Os dados confirmam que
// a Equatorial para nessas datas: a espera cresce um dia útil quando uma delas cai no meio.
//
// EXCEÇÃO — fatura impressa na hora pelo leiturista (3,7% das faturas, 123 UCs, crescendo desde
// abr/2026): a apresentação é o próprio dia da leitura, a nota sai na leitura e o boleto do pé é
// gerado de 3 a 5 dias ANTES (conferido no PDF da fatura 13735). A fatura já existe no dia da
// leitura; esperar o 4º dia útil atrasava a busca de 98% delas. Não depende da cidade; se repete
// por UC (49% quando o mês anterior foi assim, 1% quando não foi). Por isso a UC que teve fatura
// impressa na hora em algum dos 3 meses anteriores ganha a janela curta.
//
// Sem I/O e sem Date.now(): a data de hoje entra como parâmetro.

import { ehDiaUtil, type AjustesFeriados, type DataIso } from './reguaPrazosAgrupamento.ts';

/** Dia útil depois da leitura em que a fatura costuma chegar — início da janela. */
export const DU_CHEGADA_FATURA = 4;
/** Apresentação impressa na fatura — fim da janela. */
export const DU_APRESENTACAO = 7;
/** Sem fatura DEPOIS deste dia útil, a fatura está atrasada na Equatorial. */
export const DU_FATURA_ATRASADA = 9;
/** Fatura impressa na hora: apresentada até este dia útil depois da leitura. */
export const DU_IMPRESSA_NA_HORA = 1;
/** UC impressa na hora: sem fatura depois deste dia útil, ela já devia ter sido baixada. */
export const DU_IMPRESSA_ATRASADA = 3;
/** Meses para trás em que basta UMA fatura impressa na hora para a UC entrar na exceção. */
export const MESES_HISTORICO_IMPRESSA = 3;
/**
 * Prazo curto: o limite de emissão da Vigor (vencimento da Equatorial − 7 dias) cai até este
 * número de dias corridos depois de a fatura chegar. Medido em jul–out/2026: 13,5% das faturas
 * tinham 0 a 2 dias de folga entre chegar e o limite; 6% chegaram já depois dele.
 */
export const DIAS_PRAZO_CURTO = 2;
/** Fração das UCs do mês com o 9º dia útil vencido e sem fatura que caracteriza atraso geral. */
export const FRACAO_ATRASO_GERAL = 0.25;
/** Com menos UCs que isso no prazo vencido, o percentual é ruído de começo de mês. */
export const MINIMO_ATRASO_GERAL = 20;

const RE_DATA = /^\d{4}-\d{2}-\d{2}$/;
const pad = (n: number) => String(n).padStart(2, '0');

/** "YYYY-MM-DD" válido (aceita timestamp e usa só a data), ou null. */
function soData(valor: string | null | undefined): DataIso | null {
  const texto = String(valor ?? '').slice(0, 10);
  return RE_DATA.test(texto) ? texto : null;
}

/** Soma dias ou meses em UTC: com fuso local, a meia-noite de Brasília vira o dia anterior. */
function deslocar(data: DataIso, dias: number, meses = 0): DataIso {
  const [a, m, d] = data.split('-').map(Number);
  const t = new Date(Date.UTC(a, m - 1 + meses, d + dias));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** Soma N dias úteis. Não conta o dia de partida; n <= 0 devolve a própria data. */
export function somarDiasUteis(data: DataIso, n: number, ajustes?: AjustesFeriados): DataIso {
  const inicio = soData(data);
  if (!inicio) throw new Error(`Data inválida para somar dias úteis: ${JSON.stringify(data)}.`);
  // Teto de um ano: um N absurdo não pode virar laço sem fim na tela.
  const alvo = Math.min(Math.max(Math.trunc(n) || 0, 0), 365);
  let atual = inicio;
  let contados = 0;
  while (contados < alvo) {
    atual = deslocar(atual, 1);
    if (ehDiaUtil(atual, ajustes)) contados++;
  }
  return atual;
}

/**
 * Leitura prevista do mês a partir da `proxima_leitura` da fatura base. A base do mês anterior
 * (`mesesAteMesPrevisto` = 1) já aponta para o ciclo certo; base mais antiga anda para a frente
 * e base de mês POSTERIOR anda para trás — mesma regra de inicioJanelaPrevista (janelaCobranca).
 */
export function leituraPrevista(proximaLeitura: string | null | undefined, mesesAteMesPrevisto: number): DataIso | null {
  const base = soData(proximaLeitura);
  if (!base) return null;
  return mesesAteMesPrevisto === 1 ? base : deslocar(base, 0, mesesAteMesPrevisto - 1);
}

/** 'normal' = apresentação no 7º dia útil; 'impressa_na_hora' = no dia da leitura. */
export type PerfilFatura = 'normal' | 'impressa_na_hora';

export interface PrevisaoFatura {
  perfil: PerfilFatura;
  /** Leitura (prevista ou a da fatura) de onde a conta parte. */
  leitura: DataIso;
  /** Quando a fatura costuma chegar: início da janela. */
  chegada: DataIso;
  /** Apresentação prevista: fim da janela. */
  apresentacao: DataIso;
  /** Último dia no prazo. Sem fatura depois dele = atrasada. */
  limiteAtraso: DataIso;
}

export function preverFatura(
  leitura: string | null | undefined,
  ajustes?: AjustesFeriados,
  perfil: PerfilFatura = 'normal',
): PrevisaoFatura | null {
  const l = soData(leitura);
  if (!l) return null;
  if (perfil === 'impressa_na_hora') {
    return {
      perfil,
      leitura: l,
      chegada: somarDiasUteis(l, 1, ajustes),
      apresentacao: l,
      limiteAtraso: somarDiasUteis(l, DU_IMPRESSA_ATRASADA, ajustes),
    };
  }
  return {
    perfil,
    leitura: l,
    chegada: somarDiasUteis(l, DU_CHEGADA_FATURA, ajustes),
    apresentacao: somarDiasUteis(l, DU_APRESENTACAO, ajustes),
    limiteAtraso: somarDiasUteis(l, DU_FATURA_ATRASADA, ajustes),
  };
}

/** Apresentada no dia da leitura ou no dia útil seguinte: impressa na hora pelo leiturista. */
export function foiImpressaNaHora(
  leitura: string | null | undefined,
  apresentacao: string | null | undefined,
  ajustes?: AjustesFeriados,
): boolean {
  const l = soData(leitura);
  const a = soData(apresentacao);
  if (!l || !a || a < l) return false; // apresentação antes da leitura é erro de dado
  return a <= somarDiasUteis(l, DU_IMPRESSA_NA_HORA, ajustes);
}

const MESES_ABREV = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
/** 'SET/2026' → número sequencial do mês; ilegível → null. */
function ordemDoMes(mes: string | null | undefined): number | null {
  const m = String(mes ?? '').trim().toUpperCase().match(/^([A-Z]{3})\/(\d{4})$/);
  const i = m ? MESES_ABREV.indexOf(m[1]) : -1;
  return m && i >= 0 ? Number(m[2]) * 12 + i : null;
}

/**
 * Perfil da UC para o mês previsto: impressa na hora se teve fatura assim em algum dos 3 meses
 * anteriores. Medido de abr a out/2026: marca 255 UC×mês, acerta 85 (33%) e cobre 57% das
 * impressas na hora. O erro custa pouco (umas buscas a mais do robô); acertar tira 17 dos 21
 * casos em que a busca do robô passaria do limite de emissão.
 */
export function perfilDaUc(
  faturasDaUc: { mes_referencia?: string | null; leitura_atual?: string | null; data_apresentacao?: string | null }[],
  mesPrevisto: string,
  ajustes?: AjustesFeriados,
): PerfilFatura {
  const alvo = ordemDoMes(mesPrevisto);
  if (alvo === null) return 'normal';
  const recente = faturasDaUc.some((f) => {
    const m = ordemDoMes(f.mes_referencia);
    return m !== null && alvo - m >= 1 && alvo - m <= MESES_HISTORICO_IMPRESSA
      && foiImpressaNaHora(f.leitura_atual, f.data_apresentacao, ajustes);
  });
  return recente ? 'impressa_na_hora' : 'normal';
}

/**
 * Folga em dias corridos entre a chegada prevista da fatura e o limite de emissão. Negativa:
 * o limite vence antes de a fatura chegar. null sem previsão ou sem limite.
 */
export function folgaParaEmitir(previsao: PrevisaoFatura | null | undefined, limiteEmissao: string | null | undefined): number | null {
  const limite = soData(limiteEmissao);
  if (!previsao || !limite) return null;
  const [a1, m1, d1] = previsao.chegada.split('-').map(Number);
  const [a2, m2, d2] = limite.split('-').map(Number);
  return Math.round((Date.UTC(a2, m2 - 1, d2) - Date.UTC(a1, m1 - 1, d1)) / 86_400_000);
}

/** O limite de emissão cai até 2 dias depois de a fatura chegar: emitir assim que ela chegar. */
export function prazoCurto(previsao: PrevisaoFatura | null | undefined, limiteEmissao: string | null | undefined): boolean {
  const folga = folgaParaEmitir(previsao, limiteEmissao);
  return folga !== null && folga <= DIAS_PRAZO_CURTO;
}

/** A fatura ainda não chegou e já passou do 9º dia útil depois da leitura. */
export function faturaAtrasadaNaEquatorial(
  previsao: PrevisaoFatura | null | undefined,
  hoje: DataIso,
  temFatura: boolean,
): boolean {
  if (temFatura || !previsao) return false;
  return String(hoje).slice(0, 10) > previsao.limiteAtraso;
}

export interface ItemAtraso {
  /** Mês de referência da cobrança ('OUT/2026'). */
  mes: string;
  previsao: PrevisaoFatura | null | undefined;
  temFatura: boolean;
}

export interface AtrasoDoMes {
  mes: string;
  /** UCs do mês cujo 9º dia útil já passou. */
  comPrazoVencido: number;
  /** Dessas, as que continuam sem fatura. */
  atrasadas: number;
  fracao: number;
  /** Muitas ao mesmo tempo: é a Equatorial atrasando, não um problema de cada UC. */
  geral: boolean;
}

/**
 * Atraso por mês de referência: entre as UCs cujo 9º dia útil já passou, quantas seguem sem
 * fatura. Por mês, e não somado: os meses antigos, já resolvidos, diluiriam o mês que atrasou.
 */
export function atrasoPorMes(itens: ItemAtraso[], hoje: DataIso): AtrasoDoMes[] {
  const dia = String(hoje).slice(0, 10);
  const porMes = new Map<string, { comPrazoVencido: number; atrasadas: number }>();
  for (const item of itens) {
    // Impressa na hora fica fora: a Equatorial já entregou; se não chegou, a busca é que atrasou.
    if (!item.previsao || item.previsao.perfil === 'impressa_na_hora' || dia <= item.previsao.limiteAtraso) continue;
    const m = porMes.get(item.mes) ?? { comPrazoVencido: 0, atrasadas: 0 };
    m.comPrazoVencido++;
    if (!item.temFatura) m.atrasadas++;
    porMes.set(item.mes, m);
  }
  return [...porMes.entries()].map(([mes, m]) => {
    const fracao = m.comPrazoVencido ? m.atrasadas / m.comPrazoVencido : 0;
    return {
      mes,
      ...m,
      fracao,
      geral: m.comPrazoVencido >= MINIMO_ATRASO_GERAL && fracao >= FRACAO_ATRASO_GERAL,
    };
  });
}
